import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Loader } from 'lucide-react';

type SlugRedirectProps = {
  type: 'profile' | 'idea';
  children: React.ReactNode;
};

export default function SlugRedirect({ type, children }: SlugRedirectProps) {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const [resolvedId, setResolvedId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const checkAndRedirect = async () => {
      try {
        setLoading(true);
        setError(null);

        // If the ID is already a UUID, use it directly
        const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
        if (uuidRegex.test(id)) {
          setResolvedId(id);
          
          // Get the slug for the current UUID and update the URL if needed
          const { data, error: fetchError } = await supabase
            .from(type === 'profile' ? 'profiles' : 'book_ideas')
            .select('slug')
            .eq('id', id)
            .single();

          if (!fetchError && data?.slug && !window.location.pathname.endsWith(data.slug)) {
            const newPath = window.location.pathname.replace(id, data.slug);
            navigate(newPath, { replace: true });
          }
          return;
        }

        // Look up the ID using the appropriate function
        const { data: result, error: lookupError } = await supabase
          .rpc(type === 'profile' ? 'find_profile_by_slug' : 'find_book_idea_by_slug', {
            search_slug: id
          });

        if (lookupError) throw lookupError;
        
        if (!result) {
          setError(`${type === 'profile' ? 'Profile' : 'Book idea'} not found`);
          return;
        }

        setResolvedId(result);
      } catch (err) {
        console.error('Error during slug redirect:', err);
        setError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        setLoading(false);
      }
    };

    checkAndRedirect();
  }, [id, type]);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[200px]">
        <Loader className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto p-6 bg-red-50 border border-red-200 rounded-lg text-red-700">
        {error}
      </div>
    );
  }

  // Clone the children with the resolved ID
  return (
    <>
      {React.Children.map(children, child => {
        if (React.isValidElement(child) && resolvedId) {
          return React.cloneElement(child, { id: resolvedId });
        }
        return child;
      })}
    </>
  );
}